import * as Sentry from "@sentry/capacitor";
import assertNever from "assert-never";
import classNames from "classnames";
import ons from "onsenui";
import * as React from "react";
import { ReactElement } from "react";
import * as Ons from "react-onsenui";
import ReactPlayer from "react-player";
import useSessionStorage from "../../hooks/useSessionStorage";
import useWindowFocus from "../../hooks/useWindowFocus";
import { TrainingCenterBundleItemVideoEngmtQuestionId } from "../../messages";
import {
  TrainingCenterBundleItemVideoDimensions,
  TrainingCenterDisplayLanguage,
  TrainingCenterTranslated,
  TrainingCenterVideoSubtitles,
} from "../../messages/TrainingCenter";
import { STRINGS } from "../../strings";
import * as Subtitle from "../../subtitle_lib/subtitle_lib";
import {
  EngagementChosenAnswer,
  TrainingCenterBundleItemVideoEngagementQuestion,
  TrainingCenterBundleItemVideoMediaOverlay,
} from "../../system/AppState/AppStateTrainingCenter";
import { translate } from "../../translate";
import { HashMap } from "../../utils/HashMap";
import { HashSet } from "../../utils/HashSet";
import { List } from "../../utils/List";
import { If } from "../Generic/If";
import { IsDefined } from "../Generic/IsDefined";
import { PlayerOverlay } from "../VideoScreen/components/PlayerOverlay";
import { PlayerSubtitles } from "../VideoScreen/components/PlayerSubtitles";
import { getCurrentSubtitle } from "../VideoScreen/VideoScreenUtils";
import { formatDuration } from "./TrainingCenterBundleItemScreen";
import { TrainingCenterLanguagePreference } from "./TrainingCenterLanguageLookup";
import TrainingCenterVideoEngagementQuestions from "./TrainingCenterVideoEngagementQuestions";
import TrainingCenterVideoMediaOverlays from "./TrainingCenterVideoMediaOverlays/TrainingCenterVideoMediaOverlays";
import {
  FetchUpdate,
  useCountdownTimer,
  useFetchResponse,
  useImageDimensions,
  useInterval,
} from "./TrainingCenterVideoPlayer/Utils";
import { VideoPlayerEvent, VideoPlayerEventsNormalizer } from "./VideoPlayerEventsNormalizer";

export function makeTrainingCenterVideoMediaItemKey(
  videoUrl: string,
  videoLengthSeconds: number,
  key?: React.Key
): string {
  return `${key}|${videoLengthSeconds}|${videoUrl}`;
}

export function getOrientationByAspectRatio(videoAspectRatio: number) {
  return videoAspectRatio > 1 ? "HORIZONTAL" : ("VERTICAL" as const);
}

const DEFAULT_VIDEO_ASPECT_RATIO = 16 / 9;

export interface TrainingCenterVideoPlayerProps {
  videoUrl: string;
  videoThumbnailUrl: string | null;
  videoLengthSeconds: number;
  videoDimensions: TrainingCenterBundleItemVideoDimensions;
  videoSubtitles: List<TrainingCenterTranslated<TrainingCenterVideoSubtitles>>;
  engagementQuestions: List<TrainingCenterBundleItemVideoEngagementQuestion> | null;
  mediaOverlays: List<TrainingCenterBundleItemVideoMediaOverlay> | null;
  chosenEngagementAnswers: HashMap<
    TrainingCenterBundleItemVideoEngmtQuestionId,
    EngagementChosenAnswer
  >;
  closedEngagementQuestions: HashSet<TrainingCenterBundleItemVideoEngmtQuestionId>;
  languagePreference: TrainingCenterLanguagePreference;
  watchedSegments: [number, number][];
  fullscreen: boolean;
  aspectRatio: number;
  playing: boolean;
  showVideoLoading: boolean;
  onSetPlaying: (playing: boolean) => void;
  onClickEngagementAnswer: (
    question: TrainingCenterBundleItemVideoEngagementQuestion,
    answerValue: string,
    currentSecond: number
  ) => void;
  onClickCloseAnswer: (engagementQuestion: TrainingCenterBundleItemVideoEngagementQuestion) => void;
  onVideoPlayerReset: () => void;
  onVideoLoadingChange: (showLoading: boolean) => void;
  onVideoPlayerEvent: (event: VideoPlayerEvent) => void;
  onVideoPlayerError: (error: any, data?: any, hlsInstance?: any, hlsGlobal?: any) => void;
  onChangeVideoAspectRatio: (aspectRatio: number) => void;
  onChangeFullscreen: (isFullscreen: boolean) => void;
}

/**
 * Note: Changing the `videoUrl` or `videoLengthSeconds` props will have no
 * effect. If you need to change them, then you should also set the `key` prop
 * so that this component can be re-created in order to display a new video.
 * You can use the helper function `makeTrainingCenterVideoMediaItemKey` to
 * create an appropriate value for the `key` prop
 */
export const TrainingCenterVideoPlayer = React.memo(function TrainingCenterVideoPlayer(
  props: TrainingCenterVideoPlayerProps
): ReactElement {
  const { isIOSIdleForAWhile } = useWindowFocus();

  // ---- Initial Prop Values ----

  const videoUrl = React.useRef(props.videoUrl);
  const videoLengthSeconds = React.useRef(props.videoLengthSeconds);

  // ---- Refs ----

  const reactPlayerRef = React.useRef<ReactPlayer | null>(null);
  const captionsDisabled = React.useRef(false);

  const onVideoPlayerEventRef = React.useRef(props.onVideoPlayerEvent);
  onVideoPlayerEventRef.current = props.onVideoPlayerEvent;

  const videoPlayerEventsNormalizer = React.useRef(
    new VideoPlayerEventsNormalizer((event) => {
      onVideoPlayerEventRef.current(event);
    })
  );

  // ---- Player State ----
  const [playerReady, setPlayerReady] = React.useState(false);
  const [currentSeconds, setCurrentSeconds] = useSessionStorage(`seconds-${props.videoUrl}`, 0);
  const [controlsVisible, setControlsVisible] = React.useState(true);
  const [subtitlesMenuOpen, setSubtitlesMenuOpen] = React.useState(false);
  const [currentSubtitleLanguage, setCurrentSubtitleLanguage] =
    React.useState<TrainingCenterDisplayLanguage | null>(null);
  const [currentSubtitles, setCurrentSubtitles] = React.useState<
    FetchUpdate<Subtitle.SubTitleType[]>
  >({ type: "NoRequest" });
  const [hasVerticalTransition, setVerticalTransition] = React.useState(false);
  const videoAspectType = getOrientationByAspectRatio(props.aspectRatio);
  // ---- Effects ----

  React.useCallback(() => {
    props.onChangeVideoAspectRatio.call(null, props.aspectRatio);
  }, [props.onChangeVideoAspectRatio, props.aspectRatio]);

  const hideControlsIn = useCountdownTimer(() => {
    if (props.playing) {
      setControlsVisible(false);
    }
  });

  if (props.fullscreen && !hasVerticalTransition) {
    setVerticalTransition(true);
  }

  const { onClickEngagementAnswer, onClickCloseAnswer } = props;

  useImageDimensions(props.videoThumbnailUrl, (result) => {
    console.log(result.type, props.videoDimensions);
    if (props.videoDimensions.height === null || props.videoDimensions.width === null) {
      switch (result.type) {
        case "Loading":
        case "NoImageUrl":
          props.onChangeVideoAspectRatio.call(null, DEFAULT_VIDEO_ASPECT_RATIO);
          break;
        case "Error":
          console.warn("ERROR getting dimensions for thumbnail url:", props.videoThumbnailUrl);
          Sentry.captureException(
            new Error(`ERROR getting dimensions for thumbnail url: ${props.videoThumbnailUrl}`)
          );
          props.onChangeVideoAspectRatio.call(null, DEFAULT_VIDEO_ASPECT_RATIO);
          break;
        case "Dimensions":
          props.onChangeVideoAspectRatio.call(null, result.width / result.height);
          break;
        default:
          assertNever(result);
      }
    } else {
      const { width, height } = props.videoDimensions;

      props.onChangeVideoAspectRatio(width / height);
    }
  });

  const subtitlesUrl = React.useMemo(() => {
    const subtitles = props.videoSubtitles.find((x) => x.language === currentSubtitleLanguage);
    if (subtitles === undefined) {
      return null;
    } else {
      return subtitles.data.subtitlesUrl;
    }
  }, [currentSubtitleLanguage, props.videoSubtitles]);

  const subtitleParser = React.useCallback(
    async (response: Response): Promise<Subtitle.SubTitleType[]> => {
      const text = await response.text();
      return Subtitle.parse(text);
    },
    []
  );

  useFetchResponse(subtitlesUrl, subtitleParser, (update) => {
    setCurrentSubtitles(update);
  });

  const currentSubtitleText = React.useMemo(() => {
    if (currentSubtitleLanguage === null) {
      return "";
    }

    switch (currentSubtitles.type) {
      case "NoRequest":
        return "";
      case "Loading":
        return translate(STRINGS.LOADING);
      case "Error":
        return translate(STRINGS.AN_ERROR_HAS_OCCURED);
      case "ResponseAvailable":
        return getCurrentSubtitle(currentSubtitles.result, currentSeconds * 1000);
      default:
        return assertNever(currentSubtitles);
    }
  }, [currentSeconds, currentSubtitleLanguage, currentSubtitles]);

  React.useEffect(() => {
    props.onVideoPlayerReset.call(null);
  }, [props.onVideoPlayerReset]);

  // ---- Memos ----

  const playerConfig = React.useMemo(() => {
    return getPlayerConfig(props.videoThumbnailUrl);
  }, [props.videoThumbnailUrl]);

  const subtitleLanguages = React.useMemo(() => {
    return props.videoSubtitles.map((x) => TrainingCenterDisplayLanguage.unwrap(x.language));
  }, [props.videoSubtitles]);

  // This is temporary to help track down a bug where the video mysteriously
  // changes
  useInterval(() => {
    if (reactPlayerRef.current === null) {
      return;
    }

    const internalPlayer = reactPlayerRef.current.getInternalPlayer() as any;

    if (internalPlayer && internalPlayer.getVideoUrl) {
      const internalPlayerVideoUrl = internalPlayer.getVideoUrl();
      if (
        extractYoutubeVideoId(internalPlayerVideoUrl) !== extractYoutubeVideoId(videoUrl.current)
      ) {
        console.log(
          `ERROR Video URL Changed! <${internalPlayerVideoUrl}> !== <${
            videoUrl.current
          }> ${JSON.stringify(props)}`
        );
        Sentry.captureException(
          new Error(
            `ERROR Video URL Changed! <${internalPlayerVideoUrl}> !== <${
              videoUrl.current
            }> ${JSON.stringify(props)}`
          )
        );
      }
    }
  }, 1000);

  // ---- Callbacks ----

  const controlsTouched = React.useCallback(() => {
    const TIME_UNTIL_FADEOUT = 2000;
    hideControlsIn(TIME_UNTIL_FADEOUT);
  }, [hideControlsIn]);

  const handleDivClick = React.useCallback(() => {
    if (controlsVisible) {
      const player = document.querySelector(".VideoScreen__PlayerRipple");
      if (player !== null) {
        player.classList.add("VideoScreen__PlayerRippleClicked");

        setTimeout(() => {
          player.classList.remove("VideoScreen__PlayerRippleClicked");
        }, 100);
      }

      if (playerReady) {
        props.onSetPlaying.call(null, !props.playing);
      }
    } else {
      setControlsVisible(true);
    }

    if (!subtitlesMenuOpen) {
      controlsTouched();
    }
  }, [
    controlsVisible,
    subtitlesMenuOpen,
    props.onSetPlaying,
    props.playing,
    controlsTouched,
    playerReady,
  ]);

  const handleClickPlayer = React.useCallback(() => {
    if (!subtitlesMenuOpen) {
      controlsTouched();
    }

    if (playerReady) {
      props.onSetPlaying.call(null, !props.playing);
    }
  }, [props.onSetPlaying, props.playing, subtitlesMenuOpen, controlsTouched, playerReady]);

  const handleChangeProgress = React.useCallback(
    (percent: number) => {
      if (!subtitlesMenuOpen) {
        controlsTouched();
      }
      if (reactPlayerRef.current === null) {
        return;
      }

      const seconds = (percent / 100) * videoLengthSeconds.current;
      reactPlayerRef.current.seekTo(seconds, "seconds");
      videoPlayerEventsNormalizer.current.handleSeekTo(seconds);
      setCurrentSeconds(seconds);
    },
    [controlsTouched, setCurrentSeconds, subtitlesMenuOpen]
  );

  const handleReactPlayerStart = React.useCallback(() => {
    if (reactPlayerRef.current !== null) {
      reactPlayerRef.current.seekTo(currentSeconds);
    }
  }, [currentSeconds]);

  const handleReactPlayerReady = React.useCallback(() => {
    if (reactPlayerRef.current === null) {
      return;
    }
    setPlayerReady(true);

    (reactPlayerRef.current.getInternalPlayer() as any).addEventListener(
      "onStateChange",
      (e: any) => {
        if (!captionsDisabled.current && e.data === 1) {
          captionsDisabled.current = true;

          e.target.unloadModule("cc");
          e.target.unloadModule("captions");
        }
      }
    );
  }, []);

  const handleReactPlayerProgress = React.useCallback(
    (state: { played: number; playedSeconds: number; loaded: number; loadedSeconds: number }) => {
      videoPlayerEventsNormalizer.current.handleReactPlayerProgress(state);
      if (state.playedSeconds > 0) {
        setCurrentSeconds(state.playedSeconds);
      }
    },
    [setCurrentSeconds]
  );

  const handleReactPlayerPause = React.useCallback(() => {
    videoPlayerEventsNormalizer.current.handleReactPlayerPause();
    setControlsVisible(true);
    props.onSetPlaying.call(null, false);
    hideControlsIn(null);
  }, [hideControlsIn, props.onSetPlaying]);

  const handleReactPlayerPlay = React.useCallback(() => {
    videoPlayerEventsNormalizer.current.handleReactPlayerPlay();

    props.onSetPlaying.call(null, true);
    if (!subtitlesMenuOpen) {
      controlsTouched();
    }
  }, [subtitlesMenuOpen, controlsTouched, props.onSetPlaying]);

  const handleReactPlayerBufferEnd = React.useCallback(() => {
    props.onVideoLoadingChange.call(null, true);
    props.onSetPlaying.call(null, true);
  }, [props.onSetPlaying, props.onVideoLoadingChange]);

  const handleReactPlayerSeek = React.useCallback((seconds: number) => {
    videoPlayerEventsNormalizer.current.handleReactPlayerSeek(seconds);
  }, []);

  const handleReactPlayerEnded = React.useCallback(() => {
    videoPlayerEventsNormalizer.current.handleReactPlayerEnded();
    props.onSetPlaying.call(null, false);
    hideControlsIn(null);
    setControlsVisible(true);
    props.onChangeFullscreen.call(null, false);

    const el = document.querySelector(".VideoScreen__description");

    if (el !== null) {
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [props.onSetPlaying, props.onChangeFullscreen, hideControlsIn]);

  const handleFullscreenClick = React.useCallback(() => {
    const nowFullscreen = !props.fullscreen;
    const el = document.querySelector(".VideoScreen__skeleton .clusterDetailsScrollElement");

    if (el !== null) {
      el.scrollTop = 0;
    }

    const mediaWrapperEl = document.querySelector(".VideoScreen__mediaWrapper");
    if (ons.platform.isChrome() && mediaWrapperEl !== null && "fullscreen" in document) {
      document.fullscreen ? document.exitFullscreen() : mediaWrapperEl.requestFullscreen();
      return;
    }

    props.onChangeFullscreen.call(null, nowFullscreen);
  }, [props.fullscreen, props.onChangeFullscreen]);

  const handleSubtitlesMenuOpen = React.useCallback(
    (open: boolean) => {
      if (open) {
        hideControlsIn(null);
      } else {
        controlsTouched();
      }
      setSubtitlesMenuOpen(open);
    },
    [controlsTouched, hideControlsIn]
  );

  const handleSubtitleLanguageSelect = React.useCallback((language: string | null) => {
    if (language === null) {
      setCurrentSubtitleLanguage(null);
    } else {
      setCurrentSubtitleLanguage(TrainingCenterDisplayLanguage.wrap(language));
    }
  }, []);

  const handleClickEngagementAnswer = React.useCallback(
    (
      question: TrainingCenterBundleItemVideoEngagementQuestion,
      answerValue: string,
      currentSecond: number
    ) => {
      props.onSetPlaying.call(null, true);
      onClickEngagementAnswer(question, answerValue, currentSecond);
    },
    [onClickEngagementAnswer, props.onSetPlaying]
  );

  const handleCloseEngagementAnswer = React.useCallback(
    (engagementQuestion: TrainingCenterBundleItemVideoEngagementQuestion) => {
      props.onSetPlaying.call(null, true);
      onClickCloseAnswer(engagementQuestion);
    },
    [onClickCloseAnswer, props.onSetPlaying]
  );

  // ---- Render ----

  return (
    <div
      className={classNames("VideoScreen__mediaWrapper", {
        VideoScreen__mediaWrapperFullscreen: props.fullscreen,
        VideoScreen__vertical: videoAspectType === "VERTICAL",
        VideoScreen__horizontal: videoAspectType === "HORIZONTAL",
        __verticalTransition: hasVerticalTransition,
      })}
      onClick={handleDivClick}
    >
      <If
        condition={!playerReady || (!controlsVisible && props.showVideoLoading)}
        comment="Loader while video loading"
      >
        <div className="TrainingCenterVideoPlayerScreen_loaderContainer">
          <Ons.ProgressCircular className="OrientationCenterScreen_loader" indeterminate={true} />
        </div>
      </If>

      <PlayerOverlay
        isControlsVisible={controlsVisible}
        isPlaying={props.playing}
        rippleClassName="lever"
        onClickPlayer={handleClickPlayer}
      />

      <div className="player-wrapper">
        <ReactPlayer
          ref={reactPlayerRef}
          url={isIOSIdleForAWhile ? undefined : videoUrl.current}
          width="100%"
          height="100%"
          className="VideoScreen__player"
          progressInterval={200}
          controls={false}
          playsinline={true}
          playing={props.playing}
          config={playerConfig}
          onReady={handleReactPlayerReady}
          onStart={handleReactPlayerStart}
          onPlay={handleReactPlayerPlay}
          onBufferEnd={handleReactPlayerBufferEnd}
          onProgress={handleReactPlayerProgress}
          onPause={handleReactPlayerPause}
          onSeek={handleReactPlayerSeek}
          onEnded={handleReactPlayerEnded}
          onError={props.onVideoPlayerError}
        />
      </div>

      <IsDefined value={props.engagementQuestions}>
        {(engagementQuestions) => (
          <div className="media-top-controller">
            <TrainingCenterVideoEngagementQuestions
              playedSeconds={Math.floor(currentSeconds)}
              engagementQuestions={engagementQuestions}
              chosenEngagementAnswers={props.chosenEngagementAnswers}
              closedEngagementQuestions={props.closedEngagementQuestions}
              languagePreference={props.languagePreference}
              onClickAnswer={handleClickEngagementAnswer}
              onClickCloseAnswer={handleCloseEngagementAnswer}
              onBeforeTimeoutEnd={handleReactPlayerPause}
            />
          </div>
        )}
      </IsDefined>

      <VideoPlayerControls
        playedSeconds={currentSeconds}
        videoLengthSeconds={videoLengthSeconds.current}
        subtitleLanguages={subtitleLanguages}
        fullscreen={props.fullscreen}
        controlsVisible={controlsVisible}
        subtitlesMenuOpen={subtitlesMenuOpen}
        watchedSegments={props.watchedSegments}
        onProgressChange={handleChangeProgress}
        onFullscreenClick={handleFullscreenClick}
        onSubtitlesMenuOpen={handleSubtitlesMenuOpen}
        onSubtitleLanguageSelect={handleSubtitleLanguageSelect}
      />

      <IsDefined value={props.mediaOverlays}>
        {(mediaOverlays) => (
          <If
            condition={videoAspectType === "VERTICAL"}
            comment="Show media overlays on vertical only"
          >
            <TrainingCenterVideoMediaOverlays
              playedSeconds={Math.floor(currentSeconds)}
              mediaOverlays={mediaOverlays}
              languagePreference={props.languagePreference}
            />
          </If>
        )}
      </IsDefined>

      <PlayerSubtitles isFullscreen={false} currentSubtitle={currentSubtitleText} />
    </div>
  );
});

interface VideoPlayerControlsProps {
  playedSeconds: number;
  videoLengthSeconds: number;
  subtitleLanguages: List<string>;
  fullscreen: boolean;
  controlsVisible: boolean;
  subtitlesMenuOpen: boolean;
  watchedSegments: [number, number][];
  onProgressChange: (percent: number) => void;
  onFullscreenClick: () => void;
  onSubtitlesMenuOpen: (open: boolean) => void;
  onSubtitleLanguageSelect: (language: string | null) => void;
}

const VideoPlayerControls = React.memo(function VideoPlayerControls(
  props: VideoPlayerControlsProps
): ReactElement {
  const handleInputProgressChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      props.onProgressChange.call(null, parseInt(event.target.value, 10));
    },
    [props.onProgressChange]
  );

  const totalPlayed = formatDuration(parseInt(props.playedSeconds.toFixed(0), 10));
  const totalLength = formatDuration(props.videoLengthSeconds);

  const watchPercent = (props.playedSeconds / props.videoLengthSeconds) * 100;

  return (
    <div
      className={classNames({
        VideoScreen__playerControlsContainer: true,
        VideoScreen__playerControlsContainerFullscreen: props.fullscreen,
      })}
    >
      <div
        className={classNames({
          VideoScreen__playerControls: true,
          VideoScreen__playerControlsFadeIn: props.controlsVisible,
          VideoScreen__playerControlsFadeOut: !props.controlsVisible,
        })}
      >
        <div className="VideoScreen__playerControlsTop">
          <div className="VideoScreen__playerControlsDuration">
            {totalPlayed} / {totalLength}
          </div>
          <PlayerControlButtons
            subtitleLanguages={props.subtitleLanguages}
            onFullscreenClick={props.onFullscreenClick}
            onSubtitleLanguageSelect={props.onSubtitleLanguageSelect}
            onSubtitlesMenuOpen={props.onSubtitlesMenuOpen}
            subtitlesMenuOpen={props.subtitlesMenuOpen}
          />
        </div>
        {props.watchedSegments.map(([startSeconds, endSeconds], index) => {
          const startPercent = Math.min(100, (startSeconds * 100) / props.videoLengthSeconds);
          const endPercent = Math.min(100, (endSeconds * 100) / props.videoLengthSeconds);

          return (
            <div
              key={index}
              style={{
                position: "absolute",
                zIndex: 0,
                bottom: 1,
                left: `${startPercent}%`,
                width: `${endPercent - startPercent}%`,
                height: "4px",
                background: "var(--primary-color)",
                pointerEvents: "none",
              }}
            />
          );
        })}
        <input
          onChange={handleInputProgressChange}
          className="VideoScreen__playerControlsProgress"
          type="range"
          min="0"
          max="100"
          step="0.25"
          style={{
            background: `linear-gradient(to right, var(--primary-color) 0%, var(--primary-color) ${watchPercent}%, rgba(40, 56, 71, 0.6) ${watchPercent}%, rgba(40, 56, 71, 0.6) 100%)`,
          }}
          value={watchPercent}
        />
      </div>
    </div>
  );
});

interface PlayerControlButtonsProps {
  subtitleLanguages: List<string>;
  subtitlesMenuOpen: boolean;
  onFullscreenClick: () => void;
  onSubtitlesMenuOpen: (open: boolean) => void;
  onSubtitleLanguageSelect: (language: string | null) => void;
}

const PlayerControlButtons = React.memo(function PlayerControlButtons(
  props: PlayerControlButtonsProps
): ReactElement {
  const handleFullscreenClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      event.stopPropagation();
      props.onFullscreenClick.call(null);
    },
    [props.onFullscreenClick]
  );

  const handleSubtitlesButtonClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      event.stopPropagation();
      props.onSubtitlesMenuOpen.call(null, true);
    },
    [props.onSubtitlesMenuOpen]
  );

  const handleSubtitlesCancelClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      event.stopPropagation();
      props.onSubtitleLanguageSelect.call(null, null);
      props.onSubtitlesMenuOpen.call(null, false);
    },
    [props.onSubtitleLanguageSelect, props.onSubtitlesMenuOpen]
  );

  const handleSubtitleLanguageClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>, language: string) => {
      event.stopPropagation();
      props.onSubtitleLanguageSelect.call(null, language);
      props.onSubtitlesMenuOpen.call(null, false);
    },
    [props.onSubtitleLanguageSelect, props.onSubtitlesMenuOpen]
  );

  return (
    <div className="VideoScreen__playerControlsButtons">
      {!props.subtitleLanguages.isEmpty() ? (
        <>
          <div onClick={handleSubtitlesButtonClick} className="VideoScreen__playerControlsButton">
            CC
          </div>
          <div className="VideoScreen__playerControlsSubtitlesSelectionContainer">
            <div
              className={classNames({
                VideoScreen__playerControlsSubtitlesSelection: true,
                VideoScreen__playerControlsSubtitlesSelectionVisible: props.subtitlesMenuOpen,
              })}
            >
              <div
                className={classNames({
                  VideoScreen__playerControlsSubtitlesSelectionItem: true,
                  VideoScreen__playerControlsSubtitlesSelectionItemClickable:
                    props.subtitlesMenuOpen,
                })}
                onClick={handleSubtitlesCancelClick}
              >
                {translate(STRINGS.CANCEL)}
              </div>
              <div>
                {props.subtitleLanguages.map((language) => (
                  <div
                    key={language}
                    className={classNames({
                      VideoScreen__playerControlsSubtitlesSelectionItem: true,
                      VideoScreen__playerControlsSubtitlesSelectionItemClickable:
                        props.subtitlesMenuOpen,
                    })}
                    onClick={(e) => handleSubtitleLanguageClick(e, language)}
                  >
                    {language}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      ) : null}
      <div onClick={handleFullscreenClick} className="VideoScreen__playerControlsFullscreen" />
    </div>
  );
});

export function getPlayerConfig(thumbnailUrl: string | null) {
  // Reference: <https://github.com/CookPete/react-player#config-prop>

  const isIOS = /iPad|iPhone|iPod/.test(navigator.platform);

  const videoConfig = {
    file: {
      forceHLS: !isIOS,
      hlsVersion: "0.12.4",
      attributes: { poster: thumbnailUrl },
    },
    ...(ons.platform.isIOS()
      ? {
          youtube: {
            playerVars: {
              origin: "*",
            },
          },
        }
      : {}),
  };

  return videoConfig;
}

function extractYoutubeVideoId(youtubeUrl: string): string | null {
  // See
  // <https://stackoverflow.com/questions/5830387/how-do-i-find-all-youtube-video-ids-in-a-string-using-a-regex/5831191#5831191>
  const re =
    /https?:\/\/(?:[0-9A-Z-]+\.)?(?:youtu\.be\/|youtube(?:-nocookie)?\.com\S*?[^\w\s-])([\w-]{11})(?=[^\w-]|$)(?![?=&+%\w.-]*(?:['"][^<>]*>|<\/a>))[?=&+%\w.-]*/gi;

  const matches = re.exec(youtubeUrl);
  if (matches === null) {
    return null;
  }

  return matches[1];
}
